import React, { useState } from "react";
import { useTranslate } from "@refinedev/core";
import {
  Modal,
  Upload,
  Button,
  Alert,
  Descriptions,
  Table,
  Space,
  Spin,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { axiosInstance } from "../../authProvider";

const { Dragger } = Upload;

interface ImportRowError {
  rowNumber: number;
  code?: string;
  field?: string;
  message: string;
}

interface ImportResult {
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  errorCount: number;
  errors: ImportRowError[];
}

type ProductImportModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
};

export const ProductImportModal: React.FC<ProductImportModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFileList([]);
    setImporting(false);
    setResult(null);
    setError(null);
  };

  const handleImport = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) return;

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", "upsert");

      const response = await axiosInstance.post<ImportResult>(
        "/products/import",
        formData,
      );
      setResult(response.data);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          _t("products.import.importFailed", "Import failed"),
      );
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    const hadSuccess = !!result;
    reset();
    if (hadSuccess) {
      onSuccess();
    } else {
      onCancel();
    }
  };

  const errorColumns = [
    {
      title: _t("products.import.errorRow", "Row"),
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 70,
    },
    {
      title: _t("products.import.errorCode", "Code"),
      dataIndex: "code",
      key: "code",
      width: 120,
      render: (v: string | undefined) => v ?? "-",
    },
    {
      title: _t("products.import.errorField", "Field"),
      dataIndex: "field",
      key: "field",
      width: 120,
      render: (v: string | undefined) => v ?? "-",
    },
    {
      title: _t("products.import.errorMessage", "Message"),
      dataIndex: "message",
      key: "message",
    },
  ];

  return (
    <Modal
      open={open}
      title={_t("products.import.title", "Import Products")}
      onCancel={handleClose}
      footer={
        result ? (
          <Button type="primary" onClick={handleClose}>
            {_t("buttons.close", "Close")}
          </Button>
        ) : (
          <Space>
            <Button onClick={handleClose} disabled={importing}>
              {_t("buttons.cancel", "Cancel")}
            </Button>
            <Button
              type="primary"
              onClick={handleImport}
              disabled={fileList.length === 0}
              loading={importing}
            >
              {importing
                ? _t("products.import.importing", "Importing...")
                : _t("buttons.confirm", "Confirm")}
            </Button>
          </Space>
        )
      }
      destroyOnClose
      width={640}
    >
      <Spin spinning={importing}>
        {!result && (
          <Dragger
            accept=".csv"
            maxCount={1}
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: newFileList }) => setFileList(newFileList)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {_t(
                "products.import.dragText",
                "Click or drag CSV file here",
              )}
            </p>
            <p className="ant-upload-hint">
              {_t(
                "products.import.hint",
                "Only .csv files are accepted",
              )}
            </p>
          </Dragger>
        )}

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className="mt-4"
          />
        )}

        {result && (
          <div className="flex flex-col gap-4">
            <Alert
              type={result.errorCount > 0 ? "warning" : "success"}
              showIcon
              message={_t("products.import.success", "Import completed")}
            />
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item
                label={_t("products.import.totalRows", "Total rows")}
              >
                {result.totalRows}
              </Descriptions.Item>
              <Descriptions.Item
                label={_t("products.import.created", "Created")}
              >
                {result.createdCount}
              </Descriptions.Item>
              <Descriptions.Item
                label={_t("products.import.updated", "Updated")}
              >
                {result.updatedCount}
              </Descriptions.Item>
              <Descriptions.Item
                label={_t("products.import.errorsTitle", "Errors")}
              >
                {result.errorCount}
              </Descriptions.Item>
            </Descriptions>

            {result.errors.length > 0 && (
              <Table
                columns={errorColumns}
                dataSource={result.errors}
                rowKey={(_, idx) => String(idx)}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            )}
          </div>
        )}
      </Spin>
    </Modal>
  );
};
